import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { detectBrand } from '../branding/useBrand';

/**
 * Google Analytics 4 — injected ONLY on the dicelacancion.com brand
 * (`detectBrand() === 'dicelacancion'`). MixGenie / other hosts that share
 * the same CloudFront distribution do NOT load it, so the GA property
 * stays scoped to the dicelacancion brand.
 *
 * Why JS-injected and not static in `index.html`:
 *   The same `index.html` is served to every host. A static `<script>` tag
 *   would also load on mixgenie.vernalabs.net.
 *
 * SPA pageview handling:
 *   GA4's default `gtag('config', id)` only fires the FIRST pageview. For
 *   client-side route changes (which is most of dicelacancion's UX) we
 *   suppress the auto pageview with `send_page_view: false` and fire
 *   `gtag('event', 'page_view', …)` ourselves on every react-router
 *   location change. Deduped by URL to avoid double-counts on the same
 *   path (Strict-Mode dev double-renders, route hooks updating searches
 *   in a microtask, etc.).
 *
 * Configuration:
 *   `VITE_GA_MEASUREMENT_ID` — the `G-XXXXXXXXXX` ID. Empty/unset →
 *   tracker is disabled.
 *
 * Compliance note (read before serving EU traffic at scale):
 *   GA4 sets `_ga` / `_ga_*` cookies which are NOT essential under
 *   GDPR — strict reading requires explicit consent before they're set.
 *   We currently load without a consent banner (matches the existing
 *   first-party audience-tracker decision). When the cookie banner from
 *   roadmap item 7 ships, wire `gtag('consent', 'default', {...})` ahead
 *   of the load with `analytics_storage: 'denied'` and flip to
 *   `'granted'` on accept (Consent Mode v2).
 */

const GA_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID || '').trim();
// Paths we intentionally don't send to GA. Auth + admin pages aren't
// reachable on dicelacancion.com right now (conversion gate hides them)
// but the filter is here so the moment we re-enable conversion, the
// generator / history / credits funnel doesn't leak into the public
// GA view. Mirrors the SKIP_PATH_PREFIXES list in AudienceTracker.
const GA_SKIP_PREFIXES = ['/admin', '/login', '/register', '/reset-password', '/forgot-password', '/auth', '/generator', '/history', '/credits'];

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

function shouldSkip(pathname: string): boolean {
  return GA_SKIP_PREFIXES.some((p) => pathname.startsWith(p));
}

/**
 * Inject gtag.js exactly once. Sets up `window.dataLayer` + `gtag()` so
 * subsequent `gtag('event', …)` calls have something to push into.
 * The `send_page_view: false` lets us own pageview firing per SPA route.
 */
function ensureLoaded() {
  if (typeof document === 'undefined') return;
  if (document.querySelector('script[data-mg-ga="1"]')) return;
  if (!GA_ID) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer!.push(arguments);
  };
  // Order matters: `gtag('js', …)` MUST be pushed before `config`.
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { send_page_view: false });

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  s.setAttribute('data-mg-ga', '1');
  document.head.appendChild(s);
}

export function GoogleAnalytics() {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!GA_ID) return;
    if (detectBrand() !== 'dicelacancion') return;

    ensureLoaded();

    const path = location.pathname + (location.search || '');
    if (shouldSkip(location.pathname)) return;
    if (lastPath.current === path) return;
    lastPath.current = path;

    // gtag may not be defined yet if the script tag is still loading on
    // the very first render — that's fine. Calls made before gtag.js
    // finishes are queued into dataLayer and processed when the script
    // executes, so we never lose events.
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search]);

  return null;
}
